import { CaretRightOutlined } from '@ant-design/icons'
import useWindowDimensions from '@hooks/useWindowDimensions'
import { formatMoney } from '@utils/general'
import { Button, Col, Empty, Modal, Row, Spin, Table, Tabs } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TabsProps } from 'antd'

import { faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NetWorkService, validResponse } from '@utils/API'
import { CODE_SUCCESS, PaymentMethodKey } from '@utils/constan'
import moment from 'moment'
import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { RootState } from 'store'
import { DataType } from '..'
import { BillType, IResponeICB, IStudeninfor, ITransactionHistory } from '../../../types/general'
import CellRenderStatus from './CellRenderStatus'
import IconCheck from './IconCheck'
import IconThuMotPhan from './IconThuMotPhan'
import ModalDetail from './ModalDetail'

interface IProps {
  status: number
  bills: BillType[]
  currentValue: string[]
  paymentHistorys: ITransactionHistory[]
  listSelect: string[]
  setListSelect: React.Dispatch<React.SetStateAction<string[]>>
  setImgSrc: React.Dispatch<(prevState: undefined) => undefined>
  setImgSrcICB: React.Dispatch<React.SetStateAction<IResponeICB>>
  setCurrentValue: React.Dispatch<React.SetStateAction<string[]>>
}

function getCookieByKey(key: string) {
  const match = document.cookie.match(new RegExp('(^| )' + key + '=([^;]+)'))
  return match ? JSON.parse(decodeURIComponent(match[2])) : null
}

const getPositiveAmount = (value: unknown) => {
  const amount = Number(value)
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

const isBillRow = (record: DataType) => !!record._id

type InvoicePreviewData = { base64_hddt?: string; link_hddt?: string }

const base64ToPdfBlob = (base64: string) => {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }

  return new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' })
}

const resolveInvoicePreviewUrl = (data: InvoicePreviewData): { url: string; isBlob: boolean } | null => {
  if (data?.base64_hddt) {
    const blob = base64ToPdfBlob(data.base64_hddt)
    return { url: URL.createObjectURL(blob), isBlob: true }
  }
  if (data?.link_hddt) {
    return { url: data.link_hddt, isBlob: false }
  }
  return null
}

const downloadFromPreviewUrl = (url: string, fileName: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

type InvoiceType = 'regular' | 'fee'

interface InvoiceTabPreview {
  url: string
  isBlob: boolean
  fileName: string
}

interface InvoiceTabState {
  loading: boolean
  preview: InvoiceTabPreview | null
  error: string | null
  fetched: boolean
}

const createInitialInvoiceTabs = (): Record<InvoiceType, InvoiceTabState> => ({
  regular: { loading: false, preview: null, error: null, fetched: false },
  fee: { loading: false, preview: null, error: null, fetched: false },
})

const revokeInvoiceTabPreviews = (tabs: Record<InvoiceType, InvoiceTabState>) => {
  Object.values(tabs).forEach((tab) => {
    if (tab.preview?.isBlob) {
      URL.revokeObjectURL(tab.preview.url)
    }
  })
}

const TableCustom: FC<IProps> = ({
  bills,
  status,
  listSelect,
  currentValue,
  setListSelect,
  setCurrentValue,
  setImgSrc,
  setImgSrcICB,
}: IProps) => {
  const loading1 = useSelector<RootState>((state) => state.app.loading1) as boolean
  const studenInfor = useSelector<RootState, IStudeninfor | undefined>((state) => state.app.studenInfor)
  const [isLoadingLocal, setIsLoadingLocal] = useState(true)
  const [billRender, setBillRender] = useState<DataType[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [bill, setBills] = useState<DataType | undefined>(undefined)
  const [previewRecord, setPreviewRecord] = useState<DataType | null>(null)
  const [activeInvoiceTab, setActiveInvoiceTab] = useState<InvoiceType>('regular')
  const [invoiceTabs, setInvoiceTabs] = useState<Record<InvoiceType, InvoiceTabState>>(createInitialInvoiceTabs)
  const invoiceTabsRef = useRef(invoiceTabs)
  invoiceTabsRef.current = invoiceTabs
  const { Post } = NetWorkService
  const params = useParams()
  const HEGHT = 36
  const { width } = useWindowDimensions()
  const isMobile = useMemo(() => width < 640, [width]) // is mobile size if width < 640
  const STATUS = {
    chua_thu: 'Chưa thu',
    da_thu: 'Đã thu',
    thu_mot_phan: 'Thu một phần',
    dang_thanh_toan: 'Đang thanh toán',
  }

  const getStudentCode = (record: DataType) => record.ma_sv || studenInfor?.MaSV || ''

  const handleConvertData = () => {
    let item: DataType | undefined = undefined
    let countPhaiThu = 0
    let countTongThu = 0
    let countDaThu = 0
    let countConNo = 0
    let countPhiGiaoDich = 0
    let mienGiam = 0

    const chi_tiet: any[] = bills.map((bill, i) => {
      countPhaiThu += +bill.phai_thu
      mienGiam += +bill.mien_giam
      countTongThu += +bill.tong_thu
      countPhiGiaoDich += getPositiveAmount(bill.so_tien_phu_thu_tien_ich)

      if (bill.trang_thai === 1) {
        countDaThu += parseFloat(`${bill.phai_thu - bill.mien_giam}`)
      } else {
        countConNo += parseFloat(`${bill.phai_thu - bill.mien_giam}`)
      }

      item = {
        _id: '',
        height: `${HEGHT}px`,
        key: bill.so_phieu_bao + `_${i}`,
        phaiThu: `${formatMoney(countPhaiThu)}`,
        daThu: `${formatMoney(countDaThu)}`,
        mienGiam: `${formatMoney(mienGiam)}`,
        name: '',
        noidung: '',
        status: bill.IDSV,
        ma_sv: bill.ma_sv,
        expaned: '0',
        trang_thai: '',
        tongThu: `${formatMoney(countTongThu)}`,
        so_tien_phu_thu_tien_ich: countPhiGiaoDich,
        conNo: `${formatMoney(countConNo)}`,
        hocKy: '',
        loaiThu: '',
        date_line: '',
        kenh_thu: '',
        ngay_tao: '',
        ngay_thu: '',
        hoc_ky_chu: '',
        chi_tiet: '',
      }

      return {
        _id: bill._id,
        height: bill.noi_dung ? `${bill.noi_dung.split('<br>').length * HEGHT}px` : `${HEGHT}px`,
        key: bill.so_phieu_bao,
        name: bill.so_phieu_bao,
        noidung: bill.noi_dung,
        qrCode: bill?.qrCode,
        disabledSelect: !!bill.is_bat_buoc_thanh_toan,
        status:
          bill.trang_thai === 0
            ? STATUS.chua_thu
            : bill.trang_thai === 1
            ? STATUS.da_thu
            : bill.trang_thai === 3
            ? STATUS.thu_mot_phan
            : STATUS.dang_thanh_toan,
        phaiThu: formatMoney(parseFloat(`${bill.phai_thu}`)) + '',
        daThu: bill?.trang_thai === 1 ? formatMoney(parseFloat(`${bill.phai_thu}`)) : '0',
        mienGiam: formatMoney(parseFloat(`${bill.mien_giam}`)),
        conNo: bill?.trang_thai === 0 ? formatMoney(parseFloat(`${bill.phai_thu - bill.mien_giam}`)) : '0',
        ma_sv: bill.ma_sv,
        trang_thai:
          bill.trang_thai === 0
            ? STATUS.chua_thu
            : bill.trang_thai === 1
            ? STATUS.da_thu
            : bill.trang_thai === 3
            ? STATUS.thu_mot_phan
            : `${STATUS.dang_thanh_toan}-${bill?.time_pending}`,
        expaned: '0',
        hocKy: bill?.hoc_ky_chu,
        loaiThu: bill?.ma_loai_thu?.typeFeeName,
        date_line: bill.date_line ? moment(bill.date_line).format('HH:mm:ss DD/MM/YYYY') : '-',
        tongThu: formatMoney(parseFloat(`${bill.tong_thu}`)),
        kenh_thu: bill.kenh_thu,
        // ngay_tao: moment(bill.ngay_tao).format('HH:mm:ss DD/MM/YYYY'),
        ngay_tao: bill?.ngay_tao ? moment(bill.ngay_tao).format('HH:mm:ss DD/MM/YYYY') : '-',
        ngay_thu: bill.ngay_thu ? moment(bill.ngay_thu).format('HH:mm:ss DD/MM/YYYY') : '-',
        hoc_ky_chu: bill.hoc_ky_chu,
        chi_tiet: bill?.chi_tiet,
        createdAt: bill.createdAt,
        nguon_thu_hp: bill?.nguon_thu_hp,
        so_tien_phu_thu_tien_ich: bill?.so_tien_phu_thu_tien_ich,
        so_phieu_bao_group: bill?.so_phieu_bao_group,
        so_thu_tu_thanh_toan_chung: bill?.so_thu_tu_thanh_toan_chung,
        vietQRImage: bill?.qrCode,
        paymentChannel: bill?.paymentChannel ?? '',
        amount: bill?.amount ?? 0,
        transactionCode: bill?.transactionCode ?? '',
        children: bill?.chi_tiet
          ? bill?.chi_tiet.split(',').map((e, index) => ({
              key: index,
              noidung: e,
              name: bill.so_phieu_bao,
              status: bill.trang_thai,
              ma_sv: bill.ma_sv,
              phaiThu: '',
              daThu: '',
              mienGiam: '',
              conNo: '',
              expaned: '0',
              trang_thai: '',
              tongThu: '',
              hocKy: '',
              loaiThu: '',
              date_line: '',
              kenh_thu: '',
              chi_tiet: '',
            }))
          : [],
      }
    })

    item && chi_tiet.push(item)
    setBillRender(chi_tiet)
  }

  // useEffect(() => {
  //   if (bills) {
  //     handleConvertData()
  //   }
  //   setListSelect([])
  //   setCurrentValue([])
  //   // console.log(bills)
  // }, [bills])
  useEffect(() => {
    if (bills) {
      handleConvertData()
    }

    // tự động pre-select các bills bắt buộc thanh toán
    const forcedSelected: string[] = (bills ?? [])
      .filter((b: BillType) => !!b.is_bat_buoc_thanh_toan)
      .map((b: BillType) => b.so_phieu_bao)
    setListSelect(forcedSelected)
    setCurrentValue(forcedSelected)
  }, [bills])

  // Xử lý auto-select bill khi có transaction code từ cookie
  useEffect(() => {
    if (billRender.length > 0) {
      const matchingItem = billRender.find((item) => {
        const transactionData = getCookieByKey((item as any).transactionCode)
        return transactionData && (item as any).transactionCode === transactionData?.transactionCode
      })
      if (matchingItem) {
        // Chỉ select nếu paymentChannel là một trong các loại này
        if (
          (matchingItem as any).paymentChannel === PaymentMethodKey.AGRIBANK ||
          (matchingItem as any).paymentChannel === PaymentMethodKey.PVCB ||
          (matchingItem as any).paymentChannel === PaymentMethodKey.VCB ||
          (matchingItem as any).paymentChannel === 'VIETCOMBANK' ||
          (matchingItem as any).paymentChannel === PaymentMethodKey.BIDV ||
          (matchingItem as any).paymentChannel === PaymentMethodKey.VIETINBANK
        ) {
          handleSetValue(`${matchingItem.key}`, (matchingItem as any)?.so_phieu_bao_group ?? '')
        }
      }
    }
  }, [billRender])

  const handleSetValue = (value: string, so_phieu_bao_group: string) => {
    let idArray: string[] = [...currentValue]
    const isDeselecting = idArray.includes(value)

    const expandPaymentGroup = (ids: string[]): string[] => {
      const result = new Set(ids)
      billRender.forEach((row) => {
        if (!row.key) return
        const sttc = row.so_thu_tu_thanh_toan_chung
        if (typeof sttc === 'number' && sttc > 0 && result.has(String(row.key))) {
          billRender.forEach((other) => {
            if (other.key && other.so_thu_tu_thanh_toan_chung === sttc) {
              result.add(String(other.key))
            }
          })
        }
      })
      return [...result]
    }

    const collapsePaymentGroup = (ids: string[], key: string): string[] => {
      const clickedRow = billRender.find((row) => String(row.key) === key)
      const sttc = clickedRow?.so_thu_tu_thanh_toan_chung
      if (typeof sttc !== 'number' || sttc <= 0) return ids
      return ids.filter((id) => {
        const row = billRender.find((r) => String(r.key) === id)
        return row?.so_thu_tu_thanh_toan_chung !== sttc
      })
    }

    if (isDeselecting) {
      if (so_phieu_bao_group) {
        so_phieu_bao_group
          .trim()
          .split(' ')
          .forEach((e) => (idArray = idArray.filter((existingId) => existingId !== e)))
      } else {
        idArray = idArray.filter((existingId) => existingId !== value)
      }
      idArray = collapsePaymentGroup(idArray, value)
    } else {
      if (so_phieu_bao_group) {
        if (!idArray.includes(value)) {
          idArray = []
        }
        so_phieu_bao_group
          .trim()
          .split(' ')
          .forEach((e) => idArray.push(e))
      } else {
        idArray.push(value)
      }
      idArray = expandPaymentGroup(idArray)
    }

    setListSelect(idArray)
    setCurrentValue(idArray)
  }

  // const isDisabledKey = (key: string) => {
  //   return !!(bills ?? []).some((b) => b.so_phieu_bao === key && !!b.is_bat_buoc_thanh_toan)
  // }

  // const handleSetValue = (value: string, so_phieu_bao_group: string) => {
  //   let idArray: string[] = [...currentValue]

  //   if (idArray.includes(value)) {
  //     // đang bỏ chọn
  //     if (so_phieu_bao_group) {
  //       so_phieu_bao_group
  //         .trim()
  //         .split(' ')
  //         .forEach((e) => {
  //           if (!isDisabledKey(e)) {
  //             idArray = idArray.filter((existingId) => existingId !== e)
  //           }
  //         })
  //     } else {
  //       // không cho bỏ chọn nếu key bị bắt buộc
  //       if (!isDisabledKey(value)) {
  //         idArray = idArray.filter((existingId) => existingId !== value)
  //       }
  //     }
  //   } else {
  //     // thêm chọn (cho phép thêm cả khi key disabled — disabled chỉ ngăn bỏ chọn)
  //     if (so_phieu_bao_group) {
  //       if (!idArray.includes(value)) {
  //         idArray = []
  //       }
  //       so_phieu_bao_group
  //         .trim()
  //         .split(' ')
  //         .forEach((e) => idArray.push(e))
  //     } else {
  //       idArray.push(value)
  //     }
  //   }

  //   setListSelect(idArray)
  //   setCurrentValue(idArray)
  // }

  const closeInvoicePreview = () => {
    setInvoiceTabs((prev) => {
      revokeInvoiceTabPreviews(prev)
      return createInitialInvoiceTabs()
    })
    setPreviewRecord(null)
  }

  const handleOpenInvoicePreview = (record: DataType) => {
    setInvoiceTabs((prev) => {
      revokeInvoiceTabPreviews(prev)
      return createInitialInvoiceTabs()
    })

    const hasRegular = record.nguon_thu_hp != null
    setActiveInvoiceTab(hasRegular ? 'regular' : 'fee')
    setPreviewRecord(record)
  }

  useEffect(() => {
    if (!previewRecord) return

    const type = activeInvoiceTab
    const current = invoiceTabsRef.current[type]

    if (current.fetched && !current.loading) return
    if (current.loading) return

    const config =
      type === 'regular'
        ? {
            endpoint: 'preview-e-invoice',
            fileName: 'hoa-don-dien-tu.pdf',
            notFoundMessage: 'Không tìm thấy hóa đơn',
          }
        : {
            endpoint: 'preview-utility-surcharge-e-invoice',
            fileName: 'hoa-don-phi-giao-dich.pdf',
            notFoundMessage: 'Không tìm thấy hóa đơn phí giao dịch',
          }

    const studentCode = getStudentCode(previewRecord)

    if (!studentCode) {
      setInvoiceTabs((prev) => ({
        ...prev,
        [type]: {
          loading: false,
          preview: null,
          error: 'Không tìm thấy mã sinh viên',
          fetched: true,
        },
      }))
      return
    }

    setInvoiceTabs((prev) => ({
      ...prev,
      [type]: { loading: true, preview: null, error: null, fetched: true },
    }))

    ;(async () => {
      try {
        const res: any = await Post<any>({
          url: `/api/organization/${params.orgCode}/school/${params.schoolCode}/bill/${config.endpoint}`,
          body: {
            billCode: previewRecord.name,
            studentCode,
          },
        })

        if (!res || !validResponse(res) || res.code !== CODE_SUCCESS) {
          setInvoiceTabs((prev) => ({
            ...prev,
            [type]: {
              loading: false,
              preview: null,
              error: 'Có lỗi xảy ra',
              fetched: true,
            },
          }))
          return
        }

        const preview = resolveInvoicePreviewUrl(res.data?.data ?? res.data)

        setInvoiceTabs((prev) => ({
          ...prev,
          [type]: preview
            ? {
                loading: false,
                preview: { ...preview, fileName: config.fileName },
                error: null,
                fetched: true,
              }
            : {
                loading: false,
                preview: null,
                error: config.notFoundMessage,
                fetched: true,
              },
        }))
      } catch {
        setInvoiceTabs((prev) => ({
          ...prev,
          [type]: {
            loading: false,
            preview: null,
            error: 'Có lỗi xảy ra',
            fetched: true,
          },
        }))
      }
    })()
  }, [previewRecord, activeInvoiceTab, params.orgCode, params.schoolCode, Post, studenInfor])

  const handleDownloadInvoicePreview = () => {
    const current = invoiceTabs[activeInvoiceTab].preview
    if (!current) return

    if (current.isBlob) {
      downloadFromPreviewUrl(current.url, current.fileName)
      return
    }

    window.open(current.url, '_blank', 'noopener,noreferrer')
  }

  const renderInvoiceTabContent = (tabState: InvoiceTabState, title: string) => {
    if (tabState.loading) {
      return (
        <div className="flex justify-center py-20">
          <Spin />
        </div>
      )
    }

    if (tabState.error) {
      return (
        <div className="flex justify-center items-center py-20 text-center text-[#FD0606] text-[16px] font-['Roboto'] font-[400]">
          {tabState.error}
        </div>
      )
    }

    if (tabState.preview) {
      return (
        <iframe
          src={tabState.preview.url}
          className="w-full border-0"
          style={{ height: isMobile ? '50vh' : '70vh' }}
          title={title}
        />
      )
    }

    return null
  }

  const invoicePreviewTabItems: TabsProps['items'] = [
    {
      key: 'regular',
      label: 'Hóa đơn giao dịch',
      children: renderInvoiceTabContent(invoiceTabs.regular, 'Hóa đơn giao dịch'),
    },
    {
      key: 'fee',
      label: 'Hóa đơn phí GD',
      children: renderInvoiceTabContent(invoiceTabs.fee, 'Hóa đơn phí GD'),
    },
  ]

  const canDownloadInvoice = (record: DataType) =>
    record.trang_thai !== STATUS.chua_thu &&
    record.trang_thai !== STATUS.thu_mot_phan &&
    record.trang_thai !== STATUS.dang_thanh_toan

  const canShowInvoice = (record: DataType) => {
    const hasRegular = record.nguon_thu_hp != null
    const hasFee = getPositiveAmount(record.so_tien_phu_thu_tien_ich) > 0
    return isBillRow(record) && (hasRegular || hasFee) && canDownloadInvoice(record)
  }

  const isHideDateColumn = params.orgCode === 'dhqt' && (params.schoolCode === 'dh' || params.schoolCode === 'ch')

  const columnsBase: ColumnsType<DataType> = [
    {
      title: 'Số phiếu thu',
      dataIndex: 'name',
      key: 'name',
      render: (value, _) => (
        <div
          style={{ height: _.height }}
          onClick={() => {
            setBills(_)
            setIsModalOpen(true)
          }}
          className={`h-[${_.height}] flex items-center cursor-pointer text-[#045993] font-[600]`}
        >
          {value}
        </div>
      ),
    },
    {
      title: 'Nội dung',
      dataIndex: 'noidung',
      key: 'noidung',
      render: (value) => <div dangerouslySetInnerHTML={{ __html: value }} />,
    },
    {
      title: 'Học kỳ',
      dataIndex: 'hoc_ky_chu',
      key: 'hoc_ky_chu',
      render: (value) => <div className="text-right">{value}</div>,
    },
    {
      title: 'Tổng thu (VND)',
      dataIndex: 'tongThu',
      key: 'tongThu',
      render: (value) => <div className="text-right">{value}</div>,
    },
    {
      title: 'Miễn giảm (VND)',
      dataIndex: 'mienGiam',
      key: 'mienGiam',
      render: (value) => <div className="text-right">{value}</div>,
    },
    {
      title: 'Phải thu (VND)',
      dataIndex: 'phaiThu',
      key: 'phaiThu',
      render: (value) => <div className="text-right">{value}</div>,
    },
    {
      title: 'Phí giao dịch (VND)',
      dataIndex: 'so_tien_phu_thu_tien_ich',
      key: 'so_tien_phu_thu_tien_ich',
      render: (value) => <div className="text-right">{formatMoney(getPositiveAmount(value))}</div>,
    },
    {
      title: 'Loại thu',
      dataIndex: 'loaiThu',
      key: 'loaiThu',
      render: (value) => <div className="text-right">{value}</div>,
    },
    {
      title: `${status === 1 ? 'Ngày thanh toán' : 'Ngày đến hạn'}`,
      dataIndex: `${status === 1 ? 'ngay_thu' : 'date_line'}`,
      key: `${status === 1 ? 'ngay_thu' : 'date_line'}`,
      render: (value) => <div className="text-right">{value}</div>,
    },

    {
      title: 'Trạng thái',
      dataIndex: 'trang_thai',
      key: 'trang_thai',
      render: (value) => <CellRenderStatus value={value} />,
    },
    {
      title: 'Hóa đơn',
      dataIndex: 'nguon_thu_hp',
      key: 'nguon_thu_hp',
      render: (_value, record) =>
        canShowInvoice(record) ? (
          <div className="text-center">
            <FontAwesomeIcon
              icon={faEye}
              onClick={() => {
                handleOpenInvoicePreview(record)
              }}
              className="cursor-pointer text-[#045993]"
            />
          </div>
        ) : null,
    },
  ]

  const columns = columnsBase
    .filter((col) => {
      if (isHideDateColumn && (col.key === 'ngay_thu' || col.key === 'date_line')) {
        return false
      }
      if (col.key === 'so_tien_phu_thu_tien_ich') {
        return false
      }
      return true
    })
    .map((col, index, visibleColumns) => ({
      ...col,
      title:
        typeof col.title === 'string' ? (
          <span className="pl-3">{col.title}</span>
        ) : (
          col.title
        ),
      onHeaderCell: () => ({
        style: {
          textAlign: 'left' as const,
          ...(index < visibleColumns.length - 1
            ? { borderRight: '1px solid rgba(255, 255, 255, 0.2)' }
            : {}),
        },
      }),
    }))

  const renderChecked = (item) => {
    console.log(item, ': item')
    const disabled = !!item.disabledSelect
    return (
      <input
        type="checkbox"
        checked={listSelect.find((e) => e === item.key) ? true : false}
        className=" checked:bg-[#045993] w-4 h-4 border"
        disabled={disabled}
        onChange={() => {
          // console.log(item.key)
          handleSetValue(`${item.key}`, item?.so_phieu_bao_group ?? '')
        }}
        onClick={() => {
          // setImgSrc(item.qrCode)
          // setImgSrcICB(item)
          handleSetValue(`${item.key}`, item?.so_phieu_bao_group ?? '')
        }}
      />
    )
  }

  const renderCheckedPaymentPending = (item) => {
    const transactionData = getCookieByKey(item.transactionCode)
    const isMatching = item.transactionCode === transactionData?.transactionCode
    // console.log(item)
    return (
      <input type="checkbox" checked={isMatching} className="checked:bg-[#045993] w-4 h-4 border" disabled={true} />
    )
  }
  // const renderChecked = (item) => {
  //   const disabled = !!item.disabledSelect
  //   return (
  //     <input
  //       type="checkbox"
  //       checked={listSelect.find((e) => e === item.key) ? true : false}
  //       className={`checked:bg-[#045993] w-4 h-4 border ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
  //       disabled={disabled}
  //       onChange={() => {
  //         if (!disabled) {
  //           handleSetValue(`${item.key}`, item?.so_phieu_bao_group ?? '')
  //         }
  //       }}
  //       onClick={() => {
  //         setImgSrc(item.qrCode)
  //         setImgSrcICB(item)
  //         if (!disabled) {
  //           handleSetValue(`${item.key}`, item?.so_phieu_bao_group ?? '')
  //         }
  //       }}
  //     />
  //   )
  // }

  const renderIconStatus = (item) => {
    if (item.trang_thai === STATUS.chua_thu) {
      return renderChecked(item)
    } else if (item.trang_thai === STATUS.da_thu) {
      return <IconCheck />
    } else if (item.trang_thai === STATUS.thu_mot_phan) {
      return <IconThuMotPhan />
    } else if (
      item.trang_thai.includes('-') &&
      (item.paymentChannel === PaymentMethodKey.AGRIBANK ||
        item.paymentChannel === PaymentMethodKey.PVCB ||
        item.paymentChannel === PaymentMethodKey.VCB ||
        item.paymentChannel === 'VIETCOMBANK' ||
        item.paymentChannel === PaymentMethodKey.BIDV ||
        item.paymentChannel === PaymentMethodKey.VIETINBANK)
    ) {
      return renderCheckedPaymentPending(item)
    } else if (
      item.trang_thai.includes('-') &&
      item.paymentChannel !== PaymentMethodKey.AGRIBANK &&
      item.paymentChannel !== PaymentMethodKey.PVCB &&
      item.paymentChannel !== PaymentMethodKey.VCB &&
      item.paymentChannel !== 'VIETCOMBANK' &&
      item.paymentChannel !== PaymentMethodKey.BIDV &&
      item.paymentChannel !== PaymentMethodKey.VIETINBANK
    ) {
      return renderChecked(item)
    } else {
      return <></>
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (bills.length) {
        setIsLoadingLocal(!bills.length)
      } else {
        setIsLoadingLocal(false)
      }
    }, 800)
    return () => {
      clearTimeout(timer)
    }
  }, [loading1, billRender, bills])

  return (
    <div className="flex flex-col md:mt-12 mt-2">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8 md:px-10">
        {isMobile ? (
          <>
            {billRender.length ? (
              billRender
                ?.filter((e) => !!e._id)
                ?.map((item) => {
                  return (
                    <div key={item._id} className="border-solid border-2 border-[#F4F7FC]">
                      <Row gutter={[5, 5]} className="bg-[#F4F7FC] p-1.5">
                        <Col span={22}>
                          <p className="text-[16px] text-[#06163A]">Số phiếu thu</p>
                          <div
                            onClick={() => {
                              setBills(item)
                              setIsModalOpen(true)
                            }}
                            className="flex items-center cursor-pointer text-[#045993] font-[600]"
                          >
                            {item.name}
                          </div>
                        </Col>
                        <Col
                          span={2}
                          className="flex items-center w-full"
                          onClick={() => {
                            if (!item.disabledSelect) {
                              item.trang_thai === STATUS.chua_thu &&
                                handleSetValue(`${item.key}`, item?.so_phieu_bao_group ?? '')
                            }
                          }}
                        >
                          {renderIconStatus(item)}
                        </Col>
                      </Row>
                      <div className="p-5">
                        <div className="flex justify-between">
                          <span className="text-[#595959]">Phải thu (VND): </span>
                          <span>{item.phaiThu}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#595959]"> Nội dung: </span>
                          <div dangerouslySetInnerHTML={{ __html: item.noidung }} />
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#595959]">Học kỳ: </span>
                          <span>{item.hoc_ky_chu}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#595959]">Loại thu: </span>
                          <span>{item.loaiThu}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#595959]">Trạng thái: </span>
                          {/* <span>{item.trang_thai}</span> */}
                          <CellRenderStatus value={item.trang_thai} />
                        </div>
                        {canShowInvoice(item) && (
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-[#595959]">Hóa đơn: </span>
                            <button
                              type="button"
                              onClick={() => handleOpenInvoicePreview(item)}
                              className="flex items-center gap-1.5 cursor-pointer text-[#045993] font-[600] bg-transparent border-0 p-0"
                            >
                              <FontAwesomeIcon icon={faEye} />
                              <span>Xem hóa đơn</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
            ) : (
              <div className="mt-8">
                {!isLoadingLocal ? (
                  <>
                    <Empty />
                    <p className="text-center"></p>
                  </>
                ) : (
                  <Spin className="flex justify-center" />
                )}
              </div>
            )}
          </>
        ) : (
          <Table
            loading={!bills.length && isLoadingLocal}
            columns={columns}
            dataSource={billRender}
            pagination={false}
            scroll={{ x: '1550px' }}
            rowSelection={{
              // ...rowSelection,
              renderCell: (_, record) => renderIconStatus(record),
            }}
            // rowSelection={{
            //   selectedRowKeys: listSelect,
            //   onChange: (selectedRowKeys) => {
            //     setListSelect(selectedRowKeys as string[])
            //     setCurrentValue(selectedRowKeys as string[])
            //   },
            //   renderCell: (_, record) => renderIconStatus(record),
            //   getCheckboxProps: (record) => ({ disabled: !!(record as any).disabledSelect }),
            // }}
            expandable={{
              expandIcon: ({ expanded, onExpand, record }) => (
                <CaretRightOutlined
                  onClick={(e) => onExpand(record, e)}
                  rotate={expanded ? 90 : 0}
                  className={`${record.expaned === '1' ? '' : 'hidden'}`}
                />
              ),
            }}
          />
        )}
      </div>
      {/* {renderItemReposive()} */}
      <ModalDetail isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} bill={bill} />
      <Modal
        title="Xem trước hóa đơn"
        open={!!previewRecord}
        onCancel={closeInvoicePreview}
        width={isMobile ? '95%' : '80%'}
        className="invoice-preview-modal"
        wrapClassName="invoice-preview-modal"
        footer={[
          <Button key="close" onClick={closeInvoicePreview}>
            Đóng
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={handleDownloadInvoicePreview}
            disabled={!invoiceTabs[activeInvoiceTab].preview}
            className="!bg-[#045993] !border-[#045993] !text-white hover:!bg-[#045993cc] hover:!border-[#045993cc] disabled:!bg-[#39627ecc] disabled:!border-[#39627ecc]"
          >
            Tải xuống
          </Button>,
        ]}
      >
        <Tabs
          activeKey={activeInvoiceTab}
          items={invoicePreviewTabItems}
          onChange={(key) => setActiveInvoiceTab(key as InvoiceType)}
        />
      </Modal>
    </div>
  )
}

export default memo(TableCustom)
